import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  ClipboardList,
  FolderTree,
  GraduationCap,
  Layers,
  MessageSquare,
  Mic,
  ReceiptText,
  RefreshCw,
  Settings,
  ShoppingCart,
  Tag,
  Upload,
  UserPlus,
  UsersRound,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usersService, coursesService, salesService, salesTeamsService, adminService, systemService } from '../services/api';
import { SalesTeam } from '../types';
import SiteBrandedLoader, { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import DashboardAnalyticsSection, { type DashboardAnalyticsPayload } from '../components/DashboardAnalyticsSection';

type DashStatVariant = 'primary' | 'magenta' | 'gold' | 'ocean' | 'slate';

const DASH_STAT_STYLES: Record<
  DashStatVariant,
  { iconShell: string; iconTint: string; topBar: string }
> = {
  primary: {
    iconShell: 'bg-gradient-to-br from-[#1C3B9B]/14 to-[#1C3B9B]/4',
    iconTint: 'text-[#1C3B9B]',
    topBar: 'from-[#1C3B9B] via-[#2d4bb3] to-[#C11277]',
  },
  magenta: {
    iconShell: 'bg-gradient-to-br from-[#C11277]/16 to-[#C11277]/4',
    iconTint: 'text-[#C11277]',
    topBar: 'from-[#C11277] to-[#F59E0B]',
  },
  gold: {
    iconShell: 'bg-gradient-to-br from-[#F59E0B]/18 to-[#F59E0B]/5',
    iconTint: 'text-[#b45309]',
    topBar: 'from-[#F59E0B] to-[#C11277]',
  },
  ocean: {
    iconShell: 'bg-gradient-to-br from-slate-600/12 to-slate-400/5',
    iconTint: 'text-slate-600',
    topBar: 'from-slate-500 to-[#1C3B9B]',
  },
  slate: {
    iconShell: 'bg-gradient-to-br from-slate-400/18 to-slate-200/40',
    iconTint: 'text-slate-500',
    topBar: 'from-slate-400 to-slate-600',
  },
};

type AdminKpi = {
  title: string;
  value: string | number;
  variant: DashStatVariant;
  Icon: LucideIcon;
};

type QuickLink = {
  label: string;
  description: string;
  path: string;
  Icon: LucideIcon;
  accent: string;
};

const ADMIN_QUICK_LINKS: QuickLink[] = [
  {
    label: 'کاربران',
    description: 'نقش، کیف پول، خروجی',
    path: '/users-management',
    Icon: UsersRound,
    accent: 'from-blue-50 to-white border-blue-100/90 text-blue-900',
  },
  {
    label: 'سفارشات',
    description: 'وضعیت و پرداخت',
    path: '/orders',
    Icon: ShoppingCart,
    accent: 'from-violet-50 to-white border-violet-100/90 text-violet-900',
  },
  {
    label: 'لیدها',
    description: 'پیگیری فروش',
    path: '/leads',
    Icon: ClipboardList,
    accent: 'from-fuchsia-50 to-white border-fuchsia-100/90 text-fuchsia-900',
  },
  {
    label: 'کوپن‌ها',
    description: 'تخفیف و کمپین',
    path: '/coupons',
    Icon: Tag,
    accent: 'from-amber-50 to-white border-amber-100/90 text-amber-950',
  },
  {
    label: 'دوره‌ها',
    description: 'قیمت و انتشار',
    path: '/courses',
    Icon: GraduationCap,
    accent: 'from-emerald-50 to-white border-emerald-100/90 text-emerald-900',
  },
  {
    label: 'دسته‌بندی‌ها',
    description: 'استودیو محتوا',
    path: '/dashboard/categories',
    Icon: Layers,
    accent: 'from-indigo-50 to-white border-indigo-100/90 text-indigo-900',
  },
  {
    label: 'فاکتورها',
    description: 'پرداخت‌شده و مانده',
    path: '/invoices',
    Icon: ReceiptText,
    accent: 'from-slate-50 to-white border-slate-200/90 text-slate-900',
  },
  {
    label: 'پیام‌ها',
    description: 'صندوق ورودی',
    path: '/messages',
    Icon: MessageSquare,
    accent: 'from-sky-50 to-white border-sky-100/90 text-sky-900',
  },
  {
    label: 'مرکز آپلود',
    description: 'فایل و رسانه',
    path: '/upload-center',
    Icon: Upload,
    accent: 'from-orange-50 to-white border-orange-100/90 text-orange-950',
  },
  {
    label: 'تنظیمات',
    description: 'عمومی و امنیت',
    path: '/settings',
    Icon: Settings,
    accent: 'from-gray-50 to-white border-gray-200/90 text-gray-900',
  },
];

const TeamIcon = () => (
  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'SALES_MANAGER' || user?.role === 'SALES_PERSON') {
      navigate('/sales-dashboard', { replace: true });
    }
  }, [user, navigate]);

  const [stats, setStats] = useState({
    users: 0,
    sliders: 0,
    articles: 0,
    podcasts: 0,
    courses: 0,
    videoPodcasts: 0,
    salesTeam: 0,
    enrollments: 0,
    workshops: 0,
    teamMembers: 0,
    activeWorkshops: 0,
    monthlySales: 0,
    invoices: 0,
    totalSales: 0,
  });
  const [myTeam, setMyTeam] = useState<SalesTeam | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [backupLoading, setBackupLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState({
    usersWithCourses: false,
    courses: false,
    coursesWithContent: false,
  });
  const [analytics, setAnalytics] = useState<DashboardAnalyticsPayload | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsDays, setAnalyticsDays] = useState<14 | 30>(14);

  const fetchStats = useCallback(async () => {
    try {
      setError('');
      if (user?.role === 'ADMIN') {
        const statsData = await systemService.getStats();
        setStats((prev) => ({ ...prev, ...statsData }));
      } else if (user?.role === 'SALES_MANAGER') {
        try {
          const teams = await salesTeamsService.getAll();
          const managerTeam = teams.find((t: SalesTeam) => t.managerId === user.id);
          setMyTeam(managerTeam || null);
          const teamMembers = managerTeam ? managerTeam.members.length : 0;
          setStats({
            users: 0,
            sliders: 0,
            articles: 0,
            podcasts: 0,
            courses: 0,
            videoPodcasts: 0,
            salesTeam: managerTeam ? 1 : 0,
            enrollments: 0,
            workshops: 0,
            teamMembers,
            activeWorkshops: 0,
            monthlySales: 0,
            invoices: 0,
            totalSales: 0,
          });
        } catch (teamError) {
          console.error('Error fetching team data:', teamError);
          setStats({
            users: 0,
            sliders: 0,
            articles: 0,
            podcasts: 0,
            courses: 0,
            videoPodcasts: 0,
            salesTeam: 0,
            enrollments: 0,
            workshops: 0,
            teamMembers: 0,
            activeWorkshops: 0,
            monthlySales: 0,
            invoices: 0,
            totalSales: 0,
          });
        }
      } else if (user?.role === 'SALES_PERSON') {
        const myReport = await salesService.getMySalesReport();
        setStats({
          users: 0,
          sliders: 0,
          articles: 0,
          podcasts: 0,
          courses: 0,
          videoPodcasts: 0,
          salesTeam: 0,
          enrollments: myReport.enrollments || 0,
          workshops: myReport.workshops || 0,
          teamMembers: 0,
          activeWorkshops: 0,
          monthlySales: 0,
          invoices: 0,
          totalSales: 0,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دریافت آمار');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchAnalytics = useCallback(async () => {
    if (user?.role !== 'ADMIN') return;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const d = await systemService.getDashboardAnalytics(analyticsDays);
      setAnalytics(d);
    } catch (e: any) {
      setAnalyticsError(e.response?.data?.message || 'خطا در دریافت داده‌های تحلیلی');
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [user?.role, analyticsDays]);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      void fetchAnalytics();
    }
  }, [user?.role, fetchAnalytics]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      void fetchStats();
    }
  }, [user, fetchStats]);

  const handleRefresh = async () => {
    if (!user) return;
    setRefreshing(true);
    try {
      await fetchStats();
      if (user.role === 'ADMIN') {
        await fetchAnalytics();
      }
    } finally {
      setRefreshing(false);
    }
  };

  const getAdminKpis = (): AdminKpi[] => [
    { title: 'کاربران', value: stats.users, Icon: UsersRound, variant: 'primary' },
    { title: 'دوره‌ها', value: stats.courses, Icon: GraduationCap, variant: 'magenta' },
    { title: 'ثبت‌نام دوره', value: stats.enrollments, Icon: UserPlus, variant: 'gold' },
    { title: 'فاکتورها', value: stats.invoices, Icon: ReceiptText, variant: 'ocean' },
    {
      title: 'فروش پرداخت‌شده',
      value: `${Number(stats.totalSales).toLocaleString('fa-IR')} تومان`,
      Icon: Wallet,
      variant: 'primary',
    },
    { title: 'پادکست‌ها', value: stats.podcasts, Icon: Mic, variant: 'slate' },
  ];

  const getStatCards = (): AdminKpi[] | { title: string; value: string | number; Icon: LucideIcon; variant: DashStatVariant }[] => {
    if (user?.role === 'ADMIN') {
      return getAdminKpis();
    }
    if (user?.role === 'SALES_MANAGER') {
      return [
        { title: 'تیم فروش', value: stats.salesTeam, Icon: UsersRound, variant: 'primary' },
        { title: 'اعضای تیم', value: stats.teamMembers, Icon: UserPlus, variant: 'magenta' },
        { title: 'کارگاه‌های فعال', value: stats.activeWorkshops, Icon: GraduationCap, variant: 'gold' },
        { title: 'فروش ماهانه', value: stats.monthlySales, Icon: Wallet, variant: 'ocean' },
      ];
    }
    if (user?.role === 'SALES_PERSON') {
      return [
        { title: 'ثبت‌نام‌ها', value: stats.enrollments, Icon: UserPlus, variant: 'primary' },
        { title: 'کارگاه‌ها', value: stats.workshops, Icon: GraduationCap, variant: 'magenta' },
      ];
    }
    return [];
  };

  const getPageTitle = () => {
    if (user?.role === 'ADMIN') return 'داشبورد';
    if (user?.role === 'SALES_MANAGER') return 'داشبورد مدیر فروش';
    if (user?.role === 'SALES_PERSON') return 'داشبورد فروشنده';
    return 'داشبورد';
  };

  const getPageDescription = () => {
    if (user?.role === 'ADMIN') {
      return 'خلاصه وضعیت پلتفرم، میانبرهای مدیریتی و ابزار پشتیبان در یک نگاه.';
    }
    if (user?.role === 'SALES_MANAGER') return 'مدیریت تیم فروش و گزارش‌گیری';
    if (user?.role === 'SALES_PERSON') return 'گزارش فروش و مدیریت مشتریان';
    return 'داشبورد کاربری';
  };

  const handleBackup = async () => {
    try {
      setBackupLoading(true);
      const blob = await adminService.createDatabaseBackup();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      a.download = `haghighi_backup_${timestamp}.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ایجاد بکاپ');
      console.error('Backup error:', err);
    } finally {
      setBackupLoading(false);
    }
  };

  const downloadBlobFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const toCsv = (items: any[], columns: { key: string; label?: string }[]) => {
    const header = columns.map((c) => `"${(c.label || c.key).replace(/"/g, '""')}"`).join(',');
    const rows = items.map((item) =>
      columns
        .map((c) => {
          const value = item[c.key] ?? '';
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(','),
    );
    return [header, ...rows].join('\n');
  };

  const handleDownloadUsersWithCourses = async () => {
    try {
      setDownloadLoading((prev) => ({ ...prev, usersWithCourses: true }));
      const data = await usersService.exportUsersJson();
      const blob =
        data instanceof Blob ? data : new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const date = new Date().toISOString().split('T')[0];
      downloadBlobFile(blob, `users_with_courses_${date}.json`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دانلود کاربران و دوره‌ها');
      console.error('Download users with courses error:', err);
    } finally {
      setDownloadLoading((prev) => ({ ...prev, usersWithCourses: false }));
    }
  };

  const handleDownloadCourses = async () => {
    try {
      setDownloadLoading((prev) => ({ ...prev, courses: true }));
      const courses = await coursesService.getAll();
      const slimCourses = courses.map((c) => ({
        id: c.id,
        title: c.title,
        price: c.price,
        published: c.published ? 'بله' : 'خیر',
        showOnHomepage: c.showOnHomepage ? 'بله' : 'خیر',
        createdAt: new Date(c.createdAt).toLocaleDateString('fa-IR'),
        updatedAt: new Date(c.updatedAt).toLocaleDateString('fa-IR'),
      }));
      const csv = toCsv(slimCourses, [
        { key: 'id', label: 'شناسه' },
        { key: 'title', label: 'عنوان' },
        { key: 'price', label: 'قیمت' },
        { key: 'published', label: 'منتشر شده' },
        { key: 'showOnHomepage', label: 'نمایش در صفحه اصلی' },
        { key: 'createdAt', label: 'تاریخ ایجاد' },
        { key: 'updatedAt', label: 'تاریخ بروزرسانی' },
      ]);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const date = new Date().toISOString().split('T')[0];
      downloadBlobFile(blob, `courses_${date}.csv`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دانلود لیست دوره‌ها');
      console.error('Download courses error:', err);
    } finally {
      setDownloadLoading((prev) => ({ ...prev, courses: false }));
    }
  };

  const handleDownloadCoursesWithContent = async () => {
    try {
      setDownloadLoading((prev) => ({ ...prev, coursesWithContent: true }));
      const courses = await coursesService.getExportFull();
      const payload = {
        exportedAt: new Date().toISOString(),
        totalCourses: courses.length,
        courses: courses.map((c) => ({
          id: c.id,
          title: c.title,
          description: c.description,
          price: c.price,
          published: c.published,
          showOnHomepage: c.showOnHomepage,
          thumbnail: c.thumbnail,
          videoFile: c.videoFile,
          attachments: c.attachments,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          videos: c.videos ?? [],
          audios: c.audios ?? [],
        })),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const date = new Date().toISOString().split('T')[0];
      downloadBlobFile(blob, `courses_with_videos_audios_${date}.json`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دانلود لیست دوره‌ها با ویدیو و صدا');
      console.error('Download courses with content error:', err);
    } finally {
      setDownloadLoading((prev) => ({ ...prev, coursesWithContent: false }));
    }
  };

  const statCards = getStatCards();
  const todayFa = new Date().toLocaleDateString('fa-IR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const adminToolbarBtn =
    'inline-flex items-center gap-2 rounded-xl border border-slate-200/90 bg-white px-3.5 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-[#1C3B9B]/30 hover:bg-slate-50/90 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1C3B9B]/25 disabled:pointer-events-none disabled:opacity-45';

  if (loading) {
    return (
      <div className="flex min-h-[20rem] items-center justify-center rounded-2xl bg-slate-50/80 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white px-8 py-10 shadow-sm ring-1 ring-slate-900/5">
          <SiteBrandedLoader variant="section" message="در حال بارگذاری داشبورد…" className="min-h-0 bg-transparent py-0" />
        </div>
      </div>
    );
  }

  if (error && user?.role !== 'ADMIN') {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50/90 p-5 shadow-sm ring-1 ring-red-100">
        <p className="text-sm font-semibold text-red-900">{error}</p>
      </div>
    );
  }

  return (
    <div className="ios-fade-in space-y-5 rounded-2xl bg-slate-50/90 p-4 sm:p-6">
      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80 sm:p-6">
        <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-bold text-slate-500">پنل مدیریت</p>
            <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">{getPageTitle()}</h1>
            <p className="mt-2 max-w-2xl text-sm font-medium leading-relaxed text-slate-600">{getPageDescription()}</p>
            {user?.firstName ? (
              <p className="mt-2 text-sm text-slate-500">
                سلام، <span className="font-bold text-slate-800">{user.firstName}</span>
              </p>
            ) : null}
            <p className="mt-1 text-xs font-medium text-slate-400">{todayFa}</p>
          </div>
          {user?.role === 'ADMIN' ? (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
              بروزرسانی آمار
            </button>
          ) : null}
        </div>

        {error && user?.role === 'ADMIN' ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
            {error}
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {statCards.map((card, index) => {
            const vs = DASH_STAT_STYLES[card.variant];
            const Icon = card.Icon;
            return (
              <div
                key={card.title}
                className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-gradient-to-b from-white to-slate-50/50 p-5 shadow-sm transition hover:border-slate-200 hover:shadow-md"
                style={{ animationDelay: `${index * 35}ms` }}
              >
                <div
                  className={`pointer-events-none absolute inset-x-0 top-0 h-0.5 origin-right scale-x-0 bg-gradient-to-l opacity-0 transition duration-300 group-hover:scale-x-100 group-hover:opacity-100 ${vs.topBar}`}
                  aria-hidden
                />
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-500">{card.title}</p>
                    <p className="mt-2 break-words text-xl font-black tabular-nums tracking-tight text-slate-900 sm:text-2xl">
                      {card.value}
                    </p>
                  </div>
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${vs.iconShell} ${vs.iconTint}`}>
                    <Icon className="h-6 w-6" strokeWidth={2} aria-hidden />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {user?.role === 'ADMIN' && (
        <>
          <DashboardAnalyticsSection
            data={analytics}
            loading={analyticsLoading}
            error={analyticsError}
            rangeDays={analyticsDays}
            onRangeDaysChange={setAnalyticsDays}
          />
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80 sm:p-6">
            <div className="mb-4 flex flex-col gap-1 border-b border-slate-100 pb-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-900">میانبرهای مدیریت</h2>
                <p className="text-sm font-medium text-slate-600">بخش‌های پرکاربرد؛ کلیک برای باز کردن.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {ADMIN_QUICK_LINKS.map((item) => {
                const Icon = item.Icon;
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => navigate(item.path)}
                    className={`flex w-full flex-col items-start gap-2 rounded-2xl border bg-gradient-to-b p-4 text-right shadow-sm transition hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99] ${item.accent}`}
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 text-slate-700 shadow-sm ring-1 ring-black/[0.04]">
                      <Icon className="h-5 w-5" strokeWidth={2} aria-hidden />
                    </span>
                    <span className="text-sm font-black text-slate-900">{item.label}</span>
                    <span className="text-xs font-medium leading-snug text-slate-600">{item.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex flex-col gap-1 border-b border-slate-100 pb-4">
              <h2 className="text-lg font-black text-slate-900">پشتیبان‌گیری و خروجی داده</h2>
              <p className="text-sm font-medium text-slate-600">
                برای آرشیو، مهاجرت یا تحلیل خارج از سرور. بکاپ SQL نیازمند دسترسی ادمین است.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleDownloadUsersWithCourses}
                disabled={downloadLoading.usersWithCourses}
                className={adminToolbarBtn}
              >
                {downloadLoading.usersWithCourses ? (
                  <>
                    <BrandSpinnerRing size="sm" />
                    <span>در حال آماده‌سازی…</span>
                  </>
                ) : (
                  <>
                    <UsersRound className="h-4 w-4 text-indigo-600" aria-hidden />
                    <span>کاربران + دوره‌ها (JSON)</span>
                  </>
                )}
              </button>
              <button type="button" onClick={handleDownloadCourses} disabled={downloadLoading.courses} className={adminToolbarBtn}>
                {downloadLoading.courses ? (
                  <>
                    <BrandSpinnerRing size="sm" />
                    <span>در حال آماده‌سازی…</span>
                  </>
                ) : (
                  <>
                    <FolderTree className="h-4 w-4 text-rose-600" aria-hidden />
                    <span>لیست دوره‌ها (CSV)</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleDownloadCoursesWithContent}
                disabled={downloadLoading.coursesWithContent}
                className={adminToolbarBtn}
              >
                {downloadLoading.coursesWithContent ? (
                  <>
                    <BrandSpinnerRing size="sm" />
                    <span>در حال آماده‌سازی…</span>
                  </>
                ) : (
                  <>
                    <GraduationCap className="h-4 w-4 text-amber-700" aria-hidden />
                    <span>دوره‌ها + ویدیو/صوت (JSON)</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleBackup}
                disabled={backupLoading}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-l from-[#1C3B9B] to-[#C11277] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:opacity-[0.96] disabled:opacity-45"
              >
                {backupLoading ? (
                  <>
                    <BrandSpinnerRing size="sm" inverse />
                    <span>در حال ایجاد بکاپ…</span>
                  </>
                ) : (
                  <>
                    <ReceiptText className="h-4 w-4" aria-hidden />
                    <span>بکاپ دیتابیس (SQL)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {user?.role === 'SALES_MANAGER' && myTeam && (
        <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-gradient-to-l from-slate-50 to-transparent px-6 py-4">
            <h3 className="flex items-center text-lg font-black text-slate-900">
              <span className="ml-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-500 text-white">
                <TeamIcon />
              </span>
              اطلاعات تیم شما
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-3">
                <h4 className="mb-3 text-base font-bold text-slate-900">جزئیات تیم</h4>
                <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                  <span className="text-sm text-slate-500">نام تیم</span>
                  <span className="text-sm font-semibold text-slate-900">{myTeam.name}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                  <span className="text-sm text-slate-500">تعداد اعضا</span>
                  <span className="text-sm font-semibold text-slate-900">{myTeam.members.length} نفر</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                  <span className="text-sm text-slate-500">وضعیت</span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                      myTeam.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                    }`}
                  >
                    {myTeam.isActive ? 'فعال' : 'غیرفعال'}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                  <span className="text-sm text-slate-500">تاریخ ایجاد</span>
                  <span className="text-sm font-semibold text-slate-900">
                    {new Date(myTeam.createdAt).toLocaleDateString('fa-IR')}
                  </span>
                </div>
              </div>
              <div>
                <h4 className="mb-3 text-base font-bold text-slate-900">اعضای تیم</h4>
                <div className="max-h-64 space-y-2 overflow-y-auto">
                  {myTeam.members.length > 0 ? (
                    myTeam.members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center rounded-xl bg-slate-50 px-3 py-2.5 transition hover:bg-slate-100"
                      >
                        <div className="ml-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-sky-500 text-sm font-bold text-white shadow-sm">
                          {member.salesPerson.firstName?.[0] || 'ف'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {member.salesPerson.firstName} {member.salesPerson.lastName}
                          </p>
                          <p className="truncate text-xs text-slate-500">{member.salesPerson.username}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="py-8 text-center text-sm text-slate-500">هیچ عضوی در تیم شما نیست</p>
                  )}
                </div>
              </div>
            </div>
            {myTeam.description ? (
              <div className="mt-6 rounded-xl border border-slate-100 bg-slate-50/80 p-4">
                <h5 className="mb-2 text-sm font-bold text-slate-900">توضیحات تیم</h5>
                <p className="text-sm leading-relaxed text-slate-600">{myTeam.description}</p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
